import { useImpersonateToken } from '#domain/core/admin/composables/useImpersonateToken';
import { useAuthStore } from '#stores/auth.store';
import { FilterDefinitionsApi, FilterDefinitionValuesApi, HoldingApi, SupplierApi } from '@carrefour-gcs/bff-sdk';
import { ApiConfiguration } from './api.configuration';

/**
 * Class that extends ApiConfiguration to provide access to various API endpoints of BFF service API.
 *    With this class, you can easily interact with BFF service API by utilizing the pre-configured Axios instance and API configuration.
 *    And we have all type included from the OpenAPI specification for better type safety and autocompletion.
 * @class BffApiService
 * @extends ApiConfiguration
 * @example
 * const apiService = BffApiService.instance;
 * const result = await apiService.FilterDefinition.getFilterDefinition({ ... });
 */
export class BffApiService extends ApiConfiguration {
  static #instance: BffApiService;

  private readonly holding!: HoldingApi;
  private readonly supplier!: SupplierApi;
  private readonly filterDefinition!: FilterDefinitionsApi;
  private readonly filterDefinitionValue!: FilterDefinitionValuesApi;

  private constructor() {
    super('BFF', `${window.location.origin}`);

    this.overrideAxiosInstance();

    this.filterDefinition = new FilterDefinitionsApi(
      this.bffConfiguration,
      this.bffConfiguration.basePath,
      this.axiosInstance
    );
    this.filterDefinitionValue = new FilterDefinitionValuesApi(
      this.bffConfiguration,
      this.bffConfiguration.basePath,
      this.axiosInstance
    );
    this.holding = new HoldingApi(this.bffConfiguration, this.bffConfiguration.basePath, this.axiosInstance);
    this.supplier = new SupplierApi(this.bffConfiguration, this.bffConfiguration.basePath, this.axiosInstance);
  }

  /**
   * The static getter that controls access to the singleton instance.
   * Lazy initialization to avoid calling store composables before Pinia is ready
   */
  public static get instance(): BffApiService {
    if (!BffApiService.#instance) {
      BffApiService.#instance = new BffApiService();
    }

    return BffApiService.#instance;
  }

  public get FilterDefinition() {
    return this.filterDefinition;
  }

  public get FilterDefinitionValue() {
    return this.filterDefinitionValue;
  }

  public get Holding() {
    return this.holding;
  }

  public get Supplier() {
    return this.supplier;
  }

  private overrideAxiosInstance(): void {
    const authStore = useAuthStore();
    const impersonateToken = useImpersonateToken();

    this.axiosInstance.interceptors.request.use(async (config) => {
      const accessToken = authStore?.auth?.getAccessToken ? await authStore.auth.getAccessToken() : undefined;

      if (accessToken) {
        config.headers.set('Authorization', `Bearer ${accessToken}`);
      }

      if (impersonateToken.value) {
        config.headers.set('x-impersonate-token', impersonateToken.value);
      }

      return config;
    });
  }
}
